// eslint-disable-next-line max-classes-per-file
import { IModel, IViolation, IAttribute, Collection } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';
import { UrlValidatorFactoryService } from 'app/extensibility/services/validator-factory/url-validator-factory.service';
import { TimespanValidatorFactoryService } from 'app/extensibility/services/validator-factory/timespan-validator-factory.service';

import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { Dataset } from 'app/model/dataset/dataset';
import { ReferenceObjectParameters } from 'app/model/pipeline/reference-object-parameters';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { IntegrationRuntime } from '../integrationRuntime/integrationRuntime';
import { isAKVObject } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { Owned } from '../base/named';
import { Credential, CredentialTypeOptions, IValidationContext, ParameterValue } from 'shell-models-lib';
import { format, parseJsonPath, stringIsNullOrWhitespace } from 'app/shared/utils/string.util';
import { WebActivityLinkedService } from '../linkedservice/web-activity.linkedservice';
import { GuardFeatures } from 'app/services/exposure-control/ec.typings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { RestServiceLinkedService } from '../linkedservice/rest-service.linkedservice';
import { HttpServerLinkedService } from '../linkedservice/http-server.linkedservice';
import { AzureDataFactoryLinkedService } from '../linkedservice/azure-data-factory.linkedservice';
import { AzureSynapseAnalyticsWorkspaceLinkedService } from '../linkedservice/azure-synapse-analytics.linkedservice';
import { GlobalVariables } from 'app/services/globals';
import { Copier } from 'app/model/base/copier';

export enum WebActivitySPNAuthenticationType {
    WEB_SERVICEPRINCIPAL_CLIENT_ID = 'username',
    WEB_SERVICEPRINCIPAL_SECRET = 'Password',
    WEB_SERVICEPRINCIPAL_CERTIFICATE = 'Pfx'
}

export type httpAuthType = 'None' | 'Basic' | 'ClientCertificate' | 'MSI' | 'ServicePrincipal' | 'UserAssignedManagedIdentity' | 'UserAuth';
export type httpAuthRefType = 'Inline' | 'Credential';

@Metamodel.model({
    name: 'HttpAuthenticationType',
    referable: true
})
export class HttpAuthenticationType extends Owned {
    public static readonly MODEL: IModel;
    public static readonly RESOURCE: IAttribute;
    public static readonly USERTENANT: IAttribute;
    public static readonly USERNAME: IAttribute;
    public static readonly CREDENTIAL: IAttribute;

    public type: httpAuthType;
    public refType: httpAuthRefType;
    public spnAuthenticationFormat: 'Pfx' | 'Password';

    @Metamodel.attribute({
        display: stringResources.UsernameLabel,
        association: 'USERNAME',
        templatable: true
    })
    public username?: string;

    @Metamodel.attribute({
        display: stringResources.WebActivity_Pfx
    })
    public pfx?: AzureKeyVault | string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password
    })
    public password?: AzureKeyVault | string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Credential,
        reference: true,
        association: 'CREDENTIAL',
        templatable: false
    })
    public credential?: Credential;

    // this getter determines the authentication method (MSI/UAMI/SP) based on the credential reference
    public get authenticationMethod(): httpAuthType {
        if (this.credential) {
            const credType = this.credential.getType();

            switch (credType) {
                case CredentialTypeOptions.ManagedIdentity:
                    return 'MSI';
                case CredentialTypeOptions.UserAssignedManagedIdentity:
                    return 'UserAssignedManagedIdentity';
                case CredentialTypeOptions.UserAuth:
                    return 'UserAuth';
                case CredentialTypeOptions.ServicePrincipal:
                    return 'ServicePrincipal';
                default:
                    return this._authenticationMethod;
            }
        } else {
            return this._authenticationMethod;
        }
    }

    // setter for the above getter that saves the authentication method
    public set authenticationMethod(authMethod: httpAuthType) {
        this._authenticationMethod = authMethod;
    }

    @Metamodel.attribute({
        association: 'DISABLECERTVALIDATION',
        display: stringResources.ModelName_DisableCertValidation
    })
    public disableCertValidation?: boolean;

    @Metamodel.attribute({
        association: 'RESOURCE',
        templatable: true
    })
    public resource?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'USERTENANT',
        accepts: ['String']
    })
    public userTenant?: string;

    private _authenticationMethod?: httpAuthType = 'MSI';

}

export enum WebActivitySettingsType {
    INLINE = 'inline',
    LINKED_SERVICE = 'linkedService',
    CONNECTION = 'connection'
}

@Metamodel.model({
    name: 'WebActivity',
    referable: true
})
export class WebActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly URL: IAttribute;
    public static readonly RELATIVEURL: IAttribute;
    public static readonly RESOURCE: IAttribute;
    public static readonly BODY: IAttribute;
    public static readonly AUTH_PROPERTY_GROUP_SIZE = 2;
    public static readonly DISABLECERTVALIDATION: IAttribute;
    public static readonly DISABLEFOLLOWAUTHONREDIRECT: IAttribute;
    public static readonly HTTPREQUESTTIMEOUT: IAttribute;
    public static readonly TURNOFFASYNC: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly GETSTATUS_URL_PATTERN: IAttribute;
    public static readonly GETSTATUS_STATE_KEY: IAttribute;
    public static readonly GETSTATUS_SUCCESS_RESPONSE_VALUES: IAttribute;
    public static readonly GETSTATUS_FAILURE_RESPONSE_VALUES: IAttribute;
    public static readonly USEFILECONTENTFORREQUESTBODY: IAttribute;
    public static readonly FILEPATHFORREQUESTBODY: IAttribute;
    public static readonly RESPONSEJSONPATH: IAttribute;
    public activitySettings: string = __IS_FABRIC_DPE__ ? WebActivitySettingsType.CONNECTION : WebActivitySettingsType.INLINE;

    @Metamodel.attribute({
        constraint: {
            required: true
        }
    })
    public method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

    @Metamodel.attribute({
        association: 'URL',
        templatable: true,
        constraint: {
            validator: WebActivity.validateUrl
        }
    })
    public url: string;

    @Metamodel.attribute({
        association: 'RELATIVEURL',
        templatable: true
    })
    public relativeUrl: string;

    @Metamodel.attribute({
        association: 'RESOURCE',
        templatable: true
    })
    public resource?: string;

    @Metamodel.attribute({
        constraint: {
            validator: WebActivity.validateHeader
        }
    })
    public headers: ParameterConfig[];

    @Metamodel.attribute({
        templatable: true,
        association: 'BODY',
        constraint: {
            validator: WebActivity.validateBody
        },
        accepts: ['String']
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body: any;

    @Metamodel.attribute({
        constraint: {
            validator: WebActivity.validateAuthentication
        },
        type: HttpAuthenticationType.MODEL
    })
    public authentication: HttpAuthenticationType = Owned.create<HttpAuthenticationType>(HttpAuthenticationType.MODEL, this);

    @Metamodel.attribute({
        display: stringResources.IntegrationRuntime,
        reference: true
    })
    public connectVia: IntegrationRuntime;

    @Metamodel.attribute({
        display: stringResources.LinkedServices,
        reference: true,
        collection: Collection.Array,
        // Skip validation for undefined value in the Array.
        ignoreUndefinedInArray: true
    })
    public linkedServices: LinkedService[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_DataSets,
        reference: true,
        collection: Collection.Array,
        // Skip validation for undefined value in the Array.
        ignoreUndefinedInArray: true
    })
    public datasets: Dataset[] = [];

    public hasgitInfo: boolean = false;

    /**
     * Parameter value for referenced datasets.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        }
    })
    public referenceDatasetParameters: ReferenceObjectParameters[] = [];
    public userTenant?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_DisableCertValidation,
        association: 'DISABLECERTVALIDATION',
        templatable: true
    })
    public disableCertValidation?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_DisableFollowAuthOnRedirect,
        association: 'DISABLEFOLLOWAUTHONREDIRECT',
        templatable: true
    })
    public disableFollowAuthOnRedirect?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_HttpRequestTimeout,
        association: 'HTTPREQUESTTIMEOUT',
        templatable: true,
        constraint: {
            validator: WebActivity.validateHttpRequestTimeout
        }
    })
    public httpRequestTimeout: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_DisableAsync,
        association: 'TURNOFFASYNC',
        templatable: true,
    })
    public turnOffAsync?: boolean;


    @Metamodel.attribute({
        display: stringResources.ModelName_GetStatusUrlPattern,
        association: 'GETSTATUS_URL_PATTERN',
        templatable: true
    })
    public pollUrlPattern: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_GetStatusStateKey,
        association: 'GETSTATUS_STATE_KEY',
        templatable: true
    })
    public pollStatusKey: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_GetStatusSuccessResponseValues,
        association: 'GETSTATUS_SUCCESS_RESPONSE_VALUES',
        templatable: true
    })
    public pollSuccessStatusValues: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_GetStatusFailureResponseValues,
        association: 'GETSTATUS_FAILURE_RESPONSE_VALUES',
        templatable: true
    })
    public pollFailedStatusValues: string;

    @Metamodel.attribute({
        display: format(__IS_FABRIC_DPE__ ? stringResources.ModelName_ConnectionTemplate : stringResources.ModelName_LinkedServiceTemplate, stringResources.WebActivityLabel),
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: WebActivity.validateWebLinkedService
        }
    })
    public webLinkedService?: LinkedService;
    
    public linkedServiceType?: string;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_LinkedServiceForRequestBody,
        reference: true
    })
    public linkedServiceForRequestBody?: LinkedService;

    @Metamodel.attribute({
        display: stringResources.ModelName_UseFileContentForRequestBody,
        association: 'USEFILECONTENTFORREQUESTBODY',
        templatable: true,
    })
    public useFileContentForRequestBody?: boolean;

    @Metamodel.attribute({
        display: stringResources.FilePath_Label,
        constraint: {
            required: ['linkedServiceForRequestBody']
        },
        association: 'FILEPATHFORREQUESTBODY',
        templatable: true
    })
    public filePathForRequestBody?: string;

    @Metamodel.attribute({
        association: 'RESPONSEJSONPATH',
        templatable: true,
        constraint: {
            validator: WebActivity.validateResponseJsonPath
        }
    })
    public responseJsonPath?: string;

    public showDEPWarning: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
        this.authentication.type = 'None';
        this.authentication.spnAuthenticationFormat = WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET;
    }

    private static validateWebLinkedService(webActivity?: WebActivity, webLinkedService?: WebActivityLinkedService): IViolation[] | undefined {
        if (webActivity && (webActivity.activitySettings === WebActivitySettingsType.LINKED_SERVICE || webActivity.activitySettings === WebActivitySettingsType.CONNECTION)) {
            if (!webLinkedService) {
                return [
                    {
                        message: () => format(stringResources.RequiredTemplate, __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel : stringResources.LinkedService),
                        messageType: 'missing'
                    }
                ];
            } else if (webActivity.activitySettings === WebActivitySettingsType.CONNECTION) {
                const supportedConnections : string[] = [
                    Metamodel.modelFor(WebActivityLinkedService).name,
                    Metamodel.modelFor(RestServiceLinkedService).name,
                    ...GlobalVariables.enableWebConnection ? [
                        Metamodel.modelFor(HttpServerLinkedService).name,
                        Metamodel.modelFor(AzureDataFactoryLinkedService).name,
                        Metamodel.modelFor(AzureSynapseAnalyticsWorkspaceLinkedService).name,
                    ] : []
                ];
                if (!supportedConnections.includes(webLinkedService.getLinkedServiceType())) {
                    return [
                        {
                            message: () =>
                                format(
                                    stringResources.ActivityNotSupportLinkedServiceError,
                                    format(stringResources.ModelName_ActivityTemplate, stringResources.WebActivityLabel),
                                    format(stringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(webLinkedService).name)
                                ),
                            messageType: 'unsupported'
                        }
                    ];
                }
            }
        }

        return undefined;
    }

    private static validateHttpRequestTimeout(_webActivity?: WebActivity, httpRequestTimeout?: string): IViolation[] | undefined {
        if (new TimespanValidatorFactoryService().createValidator(60, 600, stringResources.HttpRequestTimeout_ErrorMessage)(httpRequestTimeout) != null) {
            return [
                {
                    message: () => stringResources.HttpRequestTimeout_ErrorMessage
                }
            ];
        }

        return undefined;
    }

    private static validateUrl(_webActivity?: WebActivity, url?: string): IViolation[] | undefined {
        const validateURLReqd = _webActivity.activitySettings === WebActivitySettingsType.INLINE;
        if (validateURLReqd && new UrlValidatorFactoryService().createValidator()(url) != null) {
            return [
                {
                    message: () => stringResources.InvalidUrlLabel
                }
            ];
        }

        return undefined;
    }

    private static validateResponseJsonPath(_webActivity?: WebActivity, responseJsonPath?: string): IViolation[] | undefined {
        // Skip validation for empty/whitespace values (optional field) and expressions, which are resolved at runtime.
        if (stringIsNullOrWhitespace(responseJsonPath) || responseJsonPath.trimStart().startsWith('@')) {
            return undefined;
        }

        // parseJsonPath returns undefined when the JSON path cannot be parsed.
        if (parseJsonPath(responseJsonPath) == null) {
            return [
                {
                    message: () => stringResources.WebActivityResponseJsonPathError
                }
            ];
        }

        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validateBody(webActivity?: WebActivity, body?: any): IViolation[] | undefined {
        if (webActivity && (webActivity.method === 'PUT' || webActivity.method === 'POST') && !webActivity.useFileContentForRequestBody) {
            if (!body) {
                return [
                    {
                        message: () => stringResources.Activity_Error_BodyRequired
                    }
                ];
            }

        }
        return undefined;
    }

    private static validateHeader(_webActivity?: WebActivity, headers?: ParameterConfig[], violation?: IViolation): IViolation[] | undefined {
        if (!headers) {
            return undefined;
        }

        if (violation && violation.context) {
            // only validate in this violation's context
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headerIndex = (violation.context as any).index;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isKey = (violation.context as any).field === 'name';

            if (headerIndex < 0 || headerIndex >= headers.length) {
                // this could happen if a header row was deleted
                return undefined;
            }

            if (isKey) {
                return !!(headers[headerIndex].name) ? undefined : [violation];
            } else {
                return !!(headers[headerIndex].value) || !!ParameterConfig.VALUE.templateValue(headers[headerIndex]) ? undefined : [violation];
            }
        } else {
            // check all the headers
            const violations = new Array<IViolation>();

            for (let i = 0; i < headers.length; ++i) {
                if (!(headers[i].name)) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderKey,
                        context: {
                            field: 'name',
                            index: i
                        }
                    });
                }

                if (!headers[i].value && !ParameterConfig.VALUE.templateValue(headers[i])) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderValue,
                        context: {
                            field: 'value',
                            index: i
                        }
                    });
                }
            }

            return violations.length > 0 ? violations : undefined;
        }
    }

    private static async validateAuthentication(_webActivity?: WebActivity, authentication?: HttpAuthenticationType, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const violations = new Array<IViolation>();
        if (authentication) {
            if (violation) {
                // only validate against this violation
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const field: string = (violation.context as any).field;

                switch (field) {
                    case 'password':
                        return authentication.password ? undefined : [violation];
                    case 'username':
                        return authentication.username ? undefined : [violation];
                    case 'pfx':
                        return authentication.pfx ? undefined : [violation];
                    case 'resource':
                        return authentication.resource ? undefined : [violation];
                    case 'usertenant':
                        return authentication.userTenant ? undefined : [violation];
                    case 'credential':
                        return authentication.credential ? undefined : [violation];
                    default:
                        break;
                }
            } else {
                // validate all the fields
                switch (authentication.type) {
                    case 'Basic':
                        if (!authentication.username && !HttpAuthenticationType.USERNAME.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_UsernameRequired,
                                context: {
                                    field: 'username'
                                }
                            });
                        }

                        if (!authentication.password) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PasswordRequired,
                                context: {
                                    field: 'password'
                                }
                            });
                        }
                        break;
                    case 'ClientCertificate':
                        if (!authentication.pfx) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PfxRequired,
                                context: {
                                    field: 'pfx'
                                }
                            });
                        } else if (_webActivity.hasgitInfo && !(isAKVObject(authentication.pfx) || authentication.pfx instanceof AzureKeyVault)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PfxAKVOnly,
                                context: {
                                    field: 'pfx'
                                }
                            });
                        }
                        const ecSvc = (await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService'));
                        if (!ecSvc.getCachedFeatureValue<boolean>(GuardFeatures.ADFOptionalPasswordToWebActivity) && !authentication.password) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PasswordRequired,
                                context: {
                                    field: 'password'
                                }
                            });
                        } else if (_webActivity.hasgitInfo && !(isAKVObject(authentication.password) || authentication.password instanceof AzureKeyVault)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_PasswordAKVOnly,
                                context: {
                                    field: 'password'
                                }
                            });
                        }

                        break;
                    case 'MSI':
                        if (!authentication.resource && !HttpAuthenticationType.RESOURCE.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_ResourceRequired,
                                context: {
                                    field: 'resource'
                                }
                            });
                        }

                        if (authentication.authenticationMethod === stringResources.ModelName_Credential && !HttpAuthenticationType.CREDENTIAL.templateValue(authentication)) {
                            violations.push({
                                message: () => stringResources.WebActivity_Error_CredentialRequired,
                                context: {
                                    field: 'credential'
                                }
                            });
                        }

                        break;
                    case 'ServicePrincipal':
                        if (authentication.authenticationMethod === stringResources.Inline) {
                            if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE &&
                                !authentication.pfx) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretRequired,
                                    context: {
                                        field: 'pfx'
                                    }
                                });
                            } else if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_CERTIFICATE &&
                                _webActivity.hasgitInfo && !(isAKVObject(authentication.pfx) || authentication.pfx instanceof AzureKeyVault)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretAKVOnly,
                                    context: {
                                        field: 'pfx'
                                    }
                                });
                            } else if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET &&
                                !authentication.password) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretRequired,
                                    context: {
                                        field: 'password'
                                    }
                                });
                            } else if (authentication.spnAuthenticationFormat === WebActivitySPNAuthenticationType.WEB_SERVICEPRINCIPAL_SECRET &&
                                _webActivity.hasgitInfo && !(isAKVObject(authentication.password) || authentication.password instanceof AzureKeyVault)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CertificateSecretAKVOnly,
                                    context: {
                                        field: 'password'
                                    }
                                });
                            }

                            if (!authentication.username && !HttpAuthenticationType.USERNAME.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_ClientIdRequired,
                                    context: {
                                        field: 'username'
                                    }
                                });
                            }
                            if (!authentication.resource && !HttpAuthenticationType.RESOURCE.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_ResourceRequired,
                                    context: {
                                        field: 'resource'
                                    }
                                });
                            }
                            if (!authentication.userTenant && !HttpAuthenticationType.USERTENANT.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_UserTenantRequired,
                                    context: {
                                        field: 'usertenant'
                                    }
                                });
                            }
                        } else if (authentication.authenticationMethod === stringResources.ModelName_Credential) {
                            if (!HttpAuthenticationType.CREDENTIAL.templateValue(authentication)) {
                                violations.push({
                                    message: () => stringResources.WebActivity_Error_CredentialRequired,
                                    context: {
                                        field: 'credential'
                                    }
                                });
                            }
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        return violations.length > 0 ? violations : undefined;
    }

    public onActivityCloned(clonedActivity: WebActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.webLinkedService?.parameterized) {
                clonedActivity.webLinkedService = Copier.clone(
                    context,
                    [this.webLinkedService],
                    undefined,
                    true
                )[0] as LinkedService;
            }
        }
    }
}
